/**
 * Список ТЗ.
 *
 * Таблица, а не карточки: здесь строки нужно сравнивать между собой —
 * кто исполнитель, что просрочено, сколько денег. Карточки для этого
 * хуже, они разносят однотипные данные по разным местам.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Person, Pill, Progress, Tabs } from '../components/ui';
import { dateTime, money, untilDeadline } from '../lib/format';
import type { Task, Video } from '../lib/types';

type Filter = 'all' | 'active' | 'done';

/**
 * Список работ.
 *
 * Админ и менеджер видят ТЗ — им важна картина заказов целиком.
 * Исполнитель видит ВИДЕО: он работает с ними, а не с контейнерами,
 * и его монтаж вообще может относиться к ТЗ, где снимает другой человек.
 */
export function TasksPage() {
  const { can } = useAuth();
  return can('task.create') ? <AdminTasks /> : <MyVideos />;
}

/** Что показывать исполнителю */
function MyVideos() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>('active');

  const { data, isLoading } = useQuery({
    queryKey: ['my-videos'],
    queryFn: () => api.get<Video[]>('/api/videos?limit=200'),
  });

  /**
   * «В работе» означает «ход за мной», а не «видео вообще не готово».
   *
   * Съёмщик снял и загрузил материал — его часть закончена, дальше
   * работает монтажер. Держать эту строку в его списке бессмысленно:
   * он ничего не может с ней сделать, а настоящие задачи тонут.
   * У монтажера то же самое, только наоборот.
   */
  const isMyTurn = (v: Video) => {
    const iShoot = v.worker.id === user?.id;
    const iEdit = v.editor?.id === user?.id;

    if (iShoot && ['NEW', 'SHOOTING'].includes(v.status)) return true;
    // В режиме без монтажера воркер доводит видео до конца сам
    if (iShoot && v.mode === 'SOLO' && v.status === 'REVISION') return true;
    if (iEdit && ['AWAITING_EDIT', 'EDITING', 'REVISION'].includes(v.status)) return true;
    return false;
  };

  const all = data ?? [];
  const videos =
    filter === 'active'
      ? all.filter(isMyTurn)
      : filter === 'done'
        ? all.filter((v) => ['REVIEW', 'DONE', 'PUBLISHED'].includes(v.status))
        : all;

  return (
    <>
      <PageHeader title="Мои задачи" sub={`${videos.length} записей`} />
      <Page>
        <Tabs
          value={filter}
          onChange={setFilter}
          items={[
            { key: 'active', label: 'Ход за мной' },
            { key: 'all', label: 'Все' },
            { key: 'done', label: 'Сданные' },
          ]}
        />

        {isLoading ? (
          <Loading rows={4} />
        ) : videos.length === 0 ? (
          <div className="card">
            <Empty
              title="Пусто"
              hint={
                filter === 'active'
                  ? 'Сейчас от тебя ничего не ждут. Загляни во вкладку «Все».'
                  : 'Нет задач в этой категории'
              }
            />
          </div>
        ) : (
          <div className="card p-0 overflow-hidden">
            {videos.map((v) => {
              const d = untilDeadline(v.currentDeadline ?? v.deadlineShootAt);
              const active = !['DONE', 'CANCELLED', 'PUBLISHED'].includes(v.status);

              /**
               * Этап определяется тем, КТО этот человек в этом видео,
               * а не статусом. Иначе монтажеру показывалась бы «съёмка»
               * и чужая ставка — просто потому, что видео ещё снимают.
               */
              const isMyShoot = v.worker.id === user?.id;
              const isMyEdit = v.editor?.id === user?.id;
              const both = isMyShoot && isMyEdit;

              const stage = both ? 'съёмка и монтаж' : isMyShoot ? 'съёмка' : 'монтаж';
              const myPrice = both
                ? v.priceShoot + v.priceEdit
                : isMyShoot
                  ? v.priceShoot
                  : v.priceEdit;

              return (
                <Link
                  key={v.id}
                  to={`/videos/${v.id}`}
                  className="flex items-center gap-4 px-4 py-3.5 border-b border-[#1A1A24]
                             last:border-0 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{v.task.title}</div>
                    <div className="text-[11.5px] text-muted2">
                      ТЗ #{v.task.number} · видео {v.index} · {stage}
                    </div>
                  </div>

                  <Pill status={v.status} />

                  {active && (
                    <div className={`text-xs w-36 text-right ${d.overdue ? 'text-dang' : 'text-muted'}`}>
                      {d.text}
                    </div>
                  )}

                  <div className="mono font-bold w-20 text-right">{money(myPrice)}</div>
                </Link>
              );
            })}
          </div>
        )}
      </Page>
    </>
  );
}

function AdminTasks() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('active');

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', filter],
    queryFn: () =>
      api.get<Task[]>(`/api/tasks?${filter === 'active' ? 'activeOnly=true' : ''}`),
  });

  const tasks = (data ?? []).filter((t) =>
    filter === 'done' ? t.progress.active === 0 : true,
  );

  return (
    <>
      <PageHeader
        title={can('task.create') ? 'Все ТЗ' : 'Мои задачи'}
        sub={`${tasks.length} записей`}
        actions={
          can('task.create') && (
            <button className="btn btn-primary" onClick={() => navigate('/tasks/new')}>
              + Создать ТЗ
            </button>
          )
        }
      />
      <Page>
        <Tabs
          value={filter}
          onChange={setFilter}
          items={[
            { key: 'active', label: 'В работе' },
            { key: 'all', label: 'Все' },
            { key: 'done', label: 'Завершённые' },
          ]}
        />

        {isLoading ? (
          <Loading rows={5} />
        ) : tasks.length === 0 ? (
          <div className="card">
            <Empty title="Пусто" hint="Нет ТЗ в этой категории" />
          </div>
        ) : (
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-[10.5px] tracking-[0.11em] text-muted2 font-bold uppercase">
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">ТЗ</th>
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Прогресс</th>
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Съёмка</th>
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Монтаж</th>
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Дедлайн</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const d = untilDeadline(t.deadlineShootAt);
                  return (
                    <tr key={t.id} className="hover:bg-white/[0.03] transition-colors">
                      <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                        <Link to={`/tasks/${t.id}`} className="block">
                          <div className="font-bold text-sm">{t.title}</div>
                          <div className="text-[11.5px] text-muted2 mt-0.5">
                            ТЗ #{t.number} · {dateTime(t.createdAt)}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3.5 border-b border-[#1A1A24] w-40">
                        <div className="flex items-center gap-2">
                          <span className="mono text-xs text-muted w-12">
                            {t.progress.done}/{t.progress.total}
                          </span>
                          <Progress percent={t.progress.percent} className="flex-1" />
                        </div>
                        <div
                          className={`text-[11px] mt-1 ${
                            t.progress.overdue ? 'text-dang' : 'text-muted2'
                          }`}
                        >
                          {t.progress.label}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                        <Person
                          id={t.worker.id}
                          name={t.worker.displayName}
                          url={t.worker.avatarUrl}
                          size={26}
                        />
                      </td>
                      <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                        {t.editor ? (
                          <Person
                            id={t.editor.id}
                            name={t.editor.displayName}
                            url={t.editor.avatarUrl}
                            size={26}
                          />
                        ) : (
                          <span className="text-muted2 text-[12.5px]">авто</span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3.5 border-b border-[#1A1A24] text-xs ${
                          d.overdue && t.progress.active > 0 ? 'text-dang' : 'text-muted'
                        }`}
                      >
                        {dateTime(t.deadlineShootAt)}
                      </td>
                      <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right mono font-bold">
                        {money((t.priceShoot + t.priceEdit) * t.quantity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Page>
    </>
  );
}
